import { useState, useEffect, useCallback } from "react";
import { 
  History, Search, Filter, RefreshCw, User, FileText, 
  DollarSign, Users, Settings, ChevronDown, Calendar,
  Eye, Clock, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

// Action labels and colors
const ACTION_CONFIG = {
  "client_created": { label: "Cliente Creado", color: "bg-green-100 text-green-800", icon: Users },
  "quotation_created": { label: "Cotización Creada", color: "bg-blue-100 text-blue-800", icon: FileText },
  "tramite_resolved": { label: "Trámite Resuelto", color: "bg-purple-100 text-purple-800", icon: FileText },
  "commission_created": { label: "Comisión Generada", color: "bg-amber-100 text-amber-800", icon: DollarSign },
  "commission_paid": { label: "Comisión Pagada", color: "bg-green-100 text-green-800", icon: DollarSign },
  "agent_created": { label: "Agente Creado", color: "bg-blue-100 text-blue-800", icon: User },
  "admin_created": { label: "Admin Creado", color: "bg-purple-100 text-purple-800", icon: User },
  "settings_updated": { label: "Config. Actualizada", color: "bg-gray-100 text-gray-800", icon: Settings },
  "default": { label: "Acción", color: "bg-gray-100 text-gray-800", icon: History }
};

const ENTITY_TYPES = [
  { value: "", label: "Todas las entidades" },
  { value: "client", label: "Clientes" },
  { value: "quotation", label: "Cotizaciones" },
  { value: "tramite", label: "Trámites" },
  { value: "commission", label: "Comisiones" },
  { value: "agent", label: "Agentes" },
  { value: "settings", label: "Configuración" }
];

export default function AuditTab() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    entity_type: "",
    user_email: "",
    action: "",
    start_date: "",
    end_date: ""
  });
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.entity_type) params.append("entity_type", filters.entity_type);
      if (filters.user_email) params.append("user_email", filters.user_email);
      if (filters.action) params.append("action", filters.action);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      params.append("limit", limit);
      params.append("skip", page * limit);
      
      const res = await axios.get(`${API}/admin/audit-logs?${params.toString()}`, axCfg);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (e) {
      console.error("Error loading audit logs:", e);
      toast.error("Error al cargar logs de auditoría");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-logs/stats`, axCfg);
      setStats(res.data);
    } catch (e) {
      console.error("Error loading audit stats:", e);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      entity_type: "",
      user_email: "",
      action: "",
      start_date: "",
      end_date: ""
    });
    setPage(0);
  };

  const openDetail = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const getActionConfig = (action) => {
    return ACTION_CONFIG[action] || ACTION_CONFIG.default;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <History className="h-6 w-6 text-[#103F38]" />
            Auditoría del Sistema
          </h2>
          <p className="text-gray-500">Historial de todas las acciones realizadas</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {total.toLocaleString()} registros
          </Badge>
          <Button 
            variant="outline" 
            onClick={() => { fetchLogs(); fetchStats(); }}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-none shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <History className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_logs?.toLocaleString() || 0}</p>
                  <p className="text-xs text-gray-500">Total de Logs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Clock className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.last_24h?.toLocaleString() || 0}</p>
                  <p className="text-xs text-gray-500">Últimas 24h</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.top_users?.length || 0}
                  </p>
                  <p className="text-xs text-gray-500">Usuarios Activos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileText className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {Object.keys(stats.by_entity || {}).length}
                  </p>
                  <p className="text-xs text-gray-500">Tipos de Entidad</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-none shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtros:</span>
            </div>
            
            <div className="w-48">
              <Select 
                value={filters.entity_type} 
                onValueChange={(v) => handleFilterChange("entity_type", v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Tipo de entidad" />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value || "all"}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-48">
              <Input
                placeholder="Buscar usuario..."
                value={filters.user_email}
                onChange={(e) => handleFilterChange("user_email", e.target.value)}
                className="h-9"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange("start_date", e.target.value)}
                className="h-9 w-36"
              />
              <span className="text-gray-400">-</span>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                className="h-9 w-36"
              />
            </div>
            
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Historial de Acciones</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center py-12 text-gray-500">No hay registros de auditoría</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => {
                const config = getActionConfig(log.action);
                const IconComponent = config.icon;
                
                return (
                  <div 
                    key={log.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => openDetail(log)}
                    data-testid={`audit-log-${log.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.color.split(' ')[0]}`}>
                        <IconComponent className={`h-4 w-4 ${config.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge className={config.color}>{config.label}</Badge>
                          <span className="text-sm font-medium text-gray-900">
                            {log.entity_type}: {log.entity_id?.slice(0, 8)}...
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          Por: {log.user_email} • {formatDate(log.timestamp)}
                        </p>
                      </div>
                    </div>
                    <Eye className="h-4 w-4 text-gray-400" />
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-gray-500">
                Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, total)} de {total}
              </p>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={(page + 1) * limit >= total}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Detalle de Auditoría
            </DialogTitle>
            <DialogDescription>
              Información completa del evento
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Acción</Label>
                  <Badge className={getActionConfig(selectedLog.action).color}>
                    {getActionConfig(selectedLog.action).label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Fecha/Hora</Label>
                  <p className="font-medium text-sm">{formatDate(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Tipo de Entidad</Label>
                  <p className="font-medium text-sm capitalize">{selectedLog.entity_type}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">ID de Entidad</Label>
                  <p className="font-medium text-sm font-mono text-xs">{selectedLog.entity_id}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-gray-500">Usuario</Label>
                <p className="font-medium text-sm">{selectedLog.user_email}</p>
              </div>
              
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <Label className="text-xs text-gray-500">Detalles</Label>
                  <div className="bg-gray-50 rounded-lg p-3 mt-1">
                    {Object.entries(selectedLog.details).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm py-1">
                        <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedLog.old_values && (
                <div>
                  <Label className="text-xs text-gray-500">Valores Anteriores</Label>
                  <div className="bg-red-50 rounded-lg p-3 mt-1">
                    {Object.entries(selectedLog.old_values).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm py-1">
                        <span className="text-gray-500">{key}:</span>
                        <span className="text-red-600">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedLog.new_values && (
                <div>
                  <Label className="text-xs text-gray-500">Valores Nuevos</Label>
                  <div className="bg-green-50 rounded-lg p-3 mt-1">
                    {Object.entries(selectedLog.new_values).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm py-1">
                        <span className="text-gray-500">{key}:</span>
                        <span className="text-green-600">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedLog.old_values && selectedLog.new_values && (
                <div className="flex items-center justify-center py-2">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="text-red-500">Anterior</span>
                    <ArrowRight className="h-4 w-4" />
                    <span className="text-green-500">Nuevo</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
